import React, { useState, useEffect } from 'react';
import { Package, Truck, MapPin, CheckCircle2, CheckCircle, AlertCircle, X, Ban, DollarSign, Mail, CreditCard, RefreshCw, ScanBarcode, Archive, ArchiveRestore, ChevronDown } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

// Status-based style helpers to avoid nested ternaries
const CANCEL_STATUS_STYLES = {
  pending: { bg: 'bg-yellow-50 border-yellow-300', icon: 'text-yellow-600', text: 'text-yellow-800', label: 'Cancellation Requested' },
  approved: { bg: 'bg-green-50 border-green-200', icon: 'text-green-600', text: 'text-green-800', label: 'Cancellation Approved' },
  default: { bg: 'bg-red-50 border-red-200', icon: 'text-red-600', text: 'text-red-800', label: 'Cancellation Rejected' },
};

function getCancelStyle(status) {
  return CANCEL_STATUS_STYLES[status] || CANCEL_STATUS_STYLES.default;
}

const REFUND_STATUS_STYLES = {
  processed: 'text-green-700',
  failed: 'text-red-700',
  default: 'text-yellow-700',
};

function getRefundColor(status) {
  return REFUND_STATUS_STYLES[status] || REFUND_STATUS_STYLES.default;
}

function getRefundLabel(order) {
  if (order.refund_status === 'processed') return `₹${order.refund_amount?.toFixed(2)} Processed`;
  if (order.refund_status === 'failed') return 'Failed';
  if (order.refund_status === 'not_applicable') return 'Not Applicable';
  return `₹${order.refund_amount?.toFixed(2)} Pending`;
}

function getStatusButtonClass(orderStatus, btnStatus) {
  if (orderStatus === btnStatus || orderStatus === 'cancelled') return 'bg-gray-200 text-gray-500 cursor-not-allowed';
  if (btnStatus === 'cancelled') return 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200';
  if (btnStatus === 'shipped') return 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200';
  return 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200';
}

function BarcodeVerify({ order }) {
  const [scanValue, setScanValue] = useState('');
  const [matchStatus, setMatchStatus] = useState(null); // null | 'match' | 'no_match'
  const [matchedProduct, setMatchedProduct] = useState('');

  const checkBarcode = async (barcode) => {
    if (!barcode.trim()) {
      setMatchStatus(null);
      return;
    }
    try {
      // Check each product in this order for barcode match
      for (const item of order.items) {
        if (!item.product_id) continue;
        const res = await api.get(`/products/${item.product_id}`);
        const product = res.data;
        if (product.barcode && product.barcode.toLowerCase() === barcode.trim().toLowerCase()) {
          setMatchStatus('match');
          setMatchedProduct(product.name);
          return;
        }
      }
      setMatchStatus('no_match');
      setMatchedProduct('');
    } catch {
      setMatchStatus('no_match');
      setMatchedProduct('');
    }
  };

  return (
    <div className="mb-4 flex items-center gap-2" data-testid={`barcode-verify-${order.tracking_id}`}>
      <div className="relative flex-1">
        <ScanBarcode className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={scanValue}
          onChange={(e) => {
            setScanValue(e.target.value);
            setMatchStatus(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              checkBarcode(scanValue);
              e.target.blur();
            }
          }}
          placeholder="Scan barcode to verify..."
          className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-[#1a3a5c]"
          autoComplete="off"
        />
      </div>
      {scanValue && !matchStatus && (
        <button onClick={() => checkBarcode(scanValue)} className="px-3 py-1.5 bg-[#1a3a5c] text-white text-sm rounded hover:bg-[#0d2240]">
          Verify
        </button>
      )}
      {matchStatus === 'match' && (
        <span className="flex items-center gap-1 text-green-600 text-sm font-medium whitespace-nowrap">
          <CheckCircle className="h-4 w-4" /> Match found — {matchedProduct}
        </span>
      )}
      {matchStatus === 'no_match' && (
        <span className="flex items-center gap-1 text-red-500 text-sm font-medium whitespace-nowrap">
          <AlertCircle className="h-4 w-4" /> No match
        </span>
      )}
      {(scanValue || matchStatus) && (
        <button onClick={() => { setScanValue(''); setMatchStatus(null); setMatchedProduct(''); }} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export default function OrdersTab({ orders, fetchData }) {
  const [shippingModal, setShippingModal] = useState({
    isOpen: false,
    orderId: null,
    estimatedDeliveryDate: '',
    courierName: '',
    trackingUrl: ''
  });

  const [cancelResponseModal, setCancelResponseModal] = useState({
    isOpen: false,
    orderId: null,
    order: null,
    adminNote: '',
    processRefund: true,
    isApproval: true
  });

  const [failedPaymentOrders, setFailedPaymentOrders] = useState([]);
  const [loadingFailedPayments, setLoadingFailedPayments] = useState(false);
  const [sendingReminder, setSendingReminder] = useState({});
  const [archivedOrders, setArchivedOrders] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [archivingOrder, setArchivingOrder] = useState({});

  // Fetch failed payment orders
  const fetchFailedPayments = async () => {
    setLoadingFailedPayments(true);
    try {
      const response = await api.get('/orders/failed-payments');
      setFailedPaymentOrders(response.data);
    } catch (error) {
    } finally {
      setLoadingFailedPayments(false);
    }
  };

  useEffect(() => {
    fetchFailedPayments();
  }, []);

  const fetchArchivedOrders = async () => {
    setLoadingArchived(true);
    try {
      const res = await api.get('/orders/archived');
      setArchivedOrders(res.data.orders || []);
    } catch (error) {
    } finally {
      setLoadingArchived(false);
    }
  };

  const archiveOrder = async (orderId) => {
    setArchivingOrder(prev => ({ ...prev, [orderId]: true }));
    try {
      await api.put(`/orders/${orderId}/archive`);
      toast.success('Order archived');
      fetchData();
      fetchFailedPayments();
      if (showArchived) fetchArchivedOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive order');
    } finally {
      setArchivingOrder(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const unarchiveOrder = async (orderId) => {
    setArchivingOrder(prev => ({ ...prev, [orderId]: true }));
    try {
      await api.put(`/orders/${orderId}/unarchive`);
      toast.success('Order restored');
      fetchArchivedOrders();
      fetchData();
      fetchFailedPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unarchive order');
    } finally {
      setArchivingOrder(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const toggleArchived = () => {
    const next = !showArchived;
    setShowArchived(next);
    if (next && archivedOrders.length === 0) fetchArchivedOrders();
  };

  // Send payment reminder
  const sendPaymentReminder = async (orderId, customerEmail) => {
    setSendingReminder(prev => ({ ...prev, [orderId]: true }));
    try {
      const response = await api.post(`/orders/${orderId}/send-payment-reminder`);
      toast.success(response.data.message || `Reminder sent to ${customerEmail}`);
      fetchFailedPayments(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const statusColors = {
    pending: 'bg-gray-100 text-gray-800',
    confirmed: 'bg-blue-100 text-blue-800',
    processing: 'bg-purple-100 text-purple-800',
    shipped: 'bg-orange-100 text-orange-800',
    out_for_delivery: 'bg-yellow-100 text-yellow-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  const statusIcons = {
    pending: <AlertCircle className="h-4 w-4" />,
    confirmed: <CheckCircle className="h-4 w-4" />,
    processing: <Package className="h-4 w-4" />,
    shipped: <Truck className="h-4 w-4" />,
    out_for_delivery: <MapPin className="h-4 w-4" />,
    delivered: <CheckCircle2 className="h-4 w-4" />,
    cancelled: <X className="h-4 w-4" />
  };

  const openShippingModal = (orderId) => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    const dateStr = defaultDate.toISOString().split('T')[0];
    
    setShippingModal({
      isOpen: true,
      orderId: orderId,
      estimatedDeliveryDate: dateStr,
      courierName: '',
      trackingUrl: ''
    });
  };

  const handleShippingConfirm = async () => {
    try {
      const response = await api.put(`/orders/${shippingModal.orderId}/status`, {
        order_status: 'shipped',
        notify_customer: true,
        estimated_delivery_date: shippingModal.estimatedDeliveryDate,
        courier_name: shippingModal.courierName || null,
        tracking_url: shippingModal.trackingUrl || null
      });
      
      if (response.data.email_sent) {
        toast.success('Order marked as shipped and customer notified via email');
      } else {
        toast.success('Order marked as shipped');
      }
      
      setShippingModal({ isOpen: false, orderId: null, estimatedDeliveryDate: '', courierName: '', trackingUrl: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    }
  };

  const updateOrderStatus = async (orderId, newStatus, notifyCustomer = true) => {
    if (newStatus === 'shipped') {
      openShippingModal(orderId);
      return;
    }
    
    try {
      const response = await api.put(`/orders/${orderId}/status`, {
        order_status: newStatus,
        notify_customer: notifyCustomer
      });
      
      if (response.data.email_sent) {
        toast.success(`Order status updated to "${newStatus}" and customer notified via email`);
      } else {
        toast.success(`Order status updated to "${newStatus}"`);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order status');
    }
  };

  const openCancelResponseModal = (order, isApproval) => {
    setCancelResponseModal({
      isOpen: true,
      orderId: order.id,
      order: order,
      adminNote: '',
      processRefund: true,
      isApproval: isApproval
    });
  };

  const handleCancellationResponse = async (approved) => {
    try {
      const response = await api.put(`/orders/${cancelResponseModal.orderId}/cancel-respond`, {
        approved: approved,
        admin_note: cancelResponseModal.adminNote || null,
        process_refund: cancelResponseModal.processRefund
      });
      
      if (approved) {
        if (response.data.refund_processed) {
          toast.success(`Cancellation approved and refund of ₹${cancelResponseModal.order?.total_amount?.toFixed(2)} processed`);
        } else {
          toast.success('Cancellation approved');
        }
      } else {
        toast.success('Cancellation request rejected');
      }
      
      setCancelResponseModal({ isOpen: false, orderId: null, order: null, adminNote: '', processRefund: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process cancellation');
    }
  };

  return (
    <div data-testid="orders-tab">
      <h2 className="font-serif text-2xl font-semibold text-[#1a3a5c] mb-6 flex items-center">
        <Package className="h-7 w-7 mr-3" />
        Order Management
      </h2>
      
      {/* Order Status Legend */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Order Status Guide:</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(statusColors).map(([status, color]) => (
            <span key={status} className={`px-3 py-1 rounded-full text-xs font-medium ${color} capitalize`}>
              {status.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      </div>

      {/* Failed Payments Section */}
      {failedPaymentOrders.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="failed-payments-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg font-semibold text-red-800 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Failed/Pending Payments ({failedPaymentOrders.length})
            </h3>
            <button
              onClick={fetchFailedPayments}
              disabled={loadingFailedPayments}
              className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-100 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loadingFailedPayments ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          <div className="space-y-3">
            {failedPaymentOrders.map((order) => (
              <div 
                key={order.id} 
                className="bg-white rounded-lg p-4 border border-red-100 flex flex-col md:flex-row md:items-center justify-between gap-4"
                data-testid={`failed-payment-${order.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">#{order.tracking_id}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      order.payment_status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {order.payment_status === 'failed' ? 'Payment Failed' : 'Payment Pending'}
                    </span>
                    {order.payment_reminder_sent && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        Reminder Sent
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    ₹{order.total_amount?.toFixed(2)} • {order.items?.length || 0} item(s) • {new Date(order.created_at).toLocaleDateString('en-IN')}
                  </p>
                  {order.customer_email && (
                    <p className="text-sm text-gray-500 mt-1">
                      <Mail className="h-3 w-3 inline mr-1" />
                      {order.customer_email}
                    </p>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {order.customer_email ? (
                    <button
                      onClick={() => sendPaymentReminder(order.id, order.customer_email)}
                      disabled={sendingReminder[order.id]}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                        sendingReminder[order.id]
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-[#0a1628] to-[#1a3a5c] text-white hover:opacity-90'
                      }`}
                      data-testid={`send-reminder-${order.id}`}
                    >
                      {sendingReminder[order.id] ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4" />
                          Send Reminder
                        </>
                      )}
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400 italic">No email available</span>
                  )}
                  <button
                    onClick={() => archiveOrder(order.id)}
                    disabled={archivingOrder[order.id]}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    data-testid={`archive-failed-${order.id}`}
                  >
                    <Archive className="h-3.5 w-3.5" />
                    {archivingOrder[order.id] ? '...' : 'Archive'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-xs text-red-600 mt-3">
            💡 Click "Send Reminder" to email customers with product images and a link to complete their purchase.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {orders.length === 0 ? (
          <div className="bg-white p-8 rounded-lg border border-gray-200 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No orders yet</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order.id} className="bg-white p-6 rounded-lg border border-gray-200" data-testid={`order-${order.id}`}>
              {/* Order Header */}
              <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-3">
                <div>
                  <h3 className="font-serif text-lg font-semibold text-[#1a3a5c]">
                    Order #{order.tracking_id}
                  </h3>
                  <p className="text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString('en-IN', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</p>
                  {order.guest_email && (
                    <p className="text-sm text-gray-500 mt-1">Guest: {order.guest_email}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    order.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {order.payment_status === 'completed' ? <CheckCircle className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                    Payment: {order.payment_status}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.order_status] || 'bg-gray-100 text-gray-800'}`}>
                    {statusIcons[order.order_status]}
                    <span className="ml-1">{(order.order_status || 'pending').replace(/_/g, ' ')}</span>
                  </span>
                </div>
              </div>
              
              {/* Order Items */}
              <div className="space-y-2 mb-4 bg-gray-50 p-3 rounded">
                {order.items.map((item, index) => (
                  <div key={`${item.product_id}-${index}`} className="flex justify-between text-sm">
                    <span className="text-gray-600">{item.product_name} x{item.quantity}</span>
                    <span className="font-medium">₹{item.total_price?.toFixed(2) || '0.00'}</span>
                  </div>
                ))}
              </div>

              {/* Barcode Verification */}
              <BarcodeVerify order={order} />
              
              {/* Order Total */}
              <div className="border-t border-gray-200 pt-3 mb-4 flex justify-between font-bold">
                <span className="text-[#1a3a5c]">Total</span>
                <span className="text-[#1a3a5c]">₹{order.total_amount?.toFixed(2) || '0.00'}</span>
              </div>
              
              {/* Shipping Address */}
              {order.shipping_address && (
                <div className="mb-4 text-sm">
                  <p className="font-medium text-gray-700 mb-1">Shipping Address:</p>
                  <p className="text-gray-600">
                    {order.shipping_address.name}, {order.shipping_address.phone}<br />
                    {order.shipping_address.address}, {order.shipping_address.city}<br />
                    {order.shipping_address.state} - {order.shipping_address.pincode}
                  </p>
                </div>
              )}
              
              {/* Delivery Information */}
              {order.estimated_delivery_date && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center text-blue-800 text-sm">
                    <Truck className="h-4 w-4 mr-2" />
                    <span className="font-medium">Estimated Delivery: {new Date(order.estimated_delivery_date).toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  </div>
                  {order.courier_name && (
                    <p className="text-sm text-blue-700 mt-1 ml-6">Courier: {order.courier_name}</p>
                  )}
                  {order.external_tracking_url && (
                    <a href={order.external_tracking_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline mt-1 ml-6 block">
                      Track on courier website →
                    </a>
                  )}
                </div>
              )}
              
              {/* Cancellation Request Alert */}
              {order.cancellation_requested && (
                <div className={`mb-4 p-4 rounded-lg border ${getCancelStyle(order.cancellation_status).bg}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center mb-2">
                        <Ban className={`h-5 w-5 mr-2 ${getCancelStyle(order.cancellation_status).icon}`} />
                        <span className={`font-medium ${getCancelStyle(order.cancellation_status).text}`}>
                          {getCancelStyle(order.cancellation_status).label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700"><strong>Reason:</strong> {order.cancellation_reason}</p>
                      {order.cancellation_details && (
                        <p className="text-sm text-gray-600 mt-1"><strong>Details:</strong> {order.cancellation_details}</p>
                      )}
                      {order.refund_status && (
                        <p className={`text-sm mt-2 font-medium ${getRefundColor(order.refund_status)}`}>
                          <DollarSign className="h-4 w-4 inline mr-1" />
                          Refund: {getRefundLabel(order)}
                        </p>
                      )}
                    </div>
                    
                    {order.cancellation_status === 'pending' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openCancelResponseModal(order, true)}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approve
                        </button>
                        <button
                          onClick={() => openCancelResponseModal(order, false)}
                          className="px-3 py-1.5 bg-red-600 text-white text-xs rounded hover:bg-red-700 flex items-center"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Order Status Update Section */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700">Update Order Status:</p>
                  {(order.order_status === 'pending' || order.order_status === 'delivered' || order.order_status === 'cancelled') && (
                    <button
                      onClick={() => archiveOrder(order.id)}
                      disabled={archivingOrder[order.id]}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                      data-testid={`archive-order-${order.id}`}
                    >
                      <Archive className="h-3.5 w-3.5" />
                      {archivingOrder[order.id] ? 'Archiving...' : 'Archive'}
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {['confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered', 'cancelled'].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        if (status === 'shipped') {
                          updateOrderStatus(order.id, status, true);
                        } else if (window.confirm(`Update order status to "${status.replace(/_/g, ' ')}"? Customer will be notified via email.`)) {
                          updateOrderStatus(order.id, status, true);
                        }
                      }}
                      disabled={order.order_status === status || order.order_status === 'cancelled'}
                      className={`px-3 py-1.5 rounded text-xs font-medium transition-all flex items-center ${
                        getStatusButtonClass(order.order_status, status)
                      }`}
                    >
                      {statusIcons[status]}
                      <span className="ml-1 capitalize">{status.replace(/_/g, ' ')}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Shipping Modal */}
      {shippingModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4 flex items-center">
              <Truck className="h-6 w-6 mr-2" />
              Mark Order as Shipped
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Delivery Date *
                </label>
                <input
                  type="date"
                  value={shippingModal.estimatedDeliveryDate}
                  onChange={(e) => setShippingModal({...shippingModal, estimatedDeliveryDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Courier / Shipping Company (Optional)
                </label>
                <input
                  type="text"
                  value={shippingModal.courierName}
                  onChange={(e) => setShippingModal({...shippingModal, courierName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                  placeholder="e.g., FedEx, DTDC, BlueDart"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  External Tracking URL (Optional)
                </label>
                <input
                  type="url"
                  value={shippingModal.trackingUrl}
                  onChange={(e) => setShippingModal({...shippingModal, trackingUrl: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                  placeholder="https://tracking.example.com/..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShippingModal({ isOpen: false, orderId: null, estimatedDeliveryDate: '', courierName: '', trackingUrl: '' })}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleShippingConfirm}
                disabled={!shippingModal.estimatedDeliveryDate}
                className="px-4 py-2 bg-[#1a3a5c] text-white rounded hover:bg-[#1f4268] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                <Truck className="h-4 w-4 mr-2" />
                Confirm Shipment
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Cancellation Response Modal */}
      {cancelResponseModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-4 flex items-center">
              <Ban className="h-6 w-6 mr-2" />
              {cancelResponseModal.isApproval ? 'Approve Cancellation' : 'Reject Cancellation'}
            </h3>
            
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p className="text-sm"><strong>Order:</strong> #{cancelResponseModal.order?.tracking_id}</p>
              <p className="text-sm"><strong>Amount:</strong> ₹{cancelResponseModal.order?.total_amount?.toFixed(2)}</p>
              <p className="text-sm"><strong>Reason:</strong> {cancelResponseModal.order?.cancellation_reason}</p>
              <p className="text-sm"><strong>Payment:</strong> {cancelResponseModal.order?.payment_status}</p>
            </div>
            
            <div className="space-y-4">
              {cancelResponseModal.isApproval && cancelResponseModal.order?.payment_status === 'completed' && (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div>
                    <p className="font-medium text-green-800">Process Razorpay Refund</p>
                    <p className="text-xs text-green-700">Automatically refund ₹{cancelResponseModal.order?.total_amount?.toFixed(2)} to customer</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cancelResponseModal.processRefund}
                      onChange={(e) => setCancelResponseModal({...cancelResponseModal, processRefund: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Response Note (Optional)
                </label>
                <textarea
                  value={cancelResponseModal.adminNote}
                  onChange={(e) => setCancelResponseModal({...cancelResponseModal, adminNote: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c]"
                  rows={3}
                  placeholder={cancelResponseModal.isApproval 
                    ? "e.g., Cancellation approved. Refund will be processed within 5-7 business days."
                    : "e.g., Order has already been shipped and cannot be cancelled."}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setCancelResponseModal({ isOpen: false, orderId: null, order: null, adminNote: '', processRefund: true })}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCancellationResponse(cancelResponseModal.isApproval)}
                className={`px-4 py-2 text-white rounded flex items-center ${
                  cancelResponseModal.isApproval 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {cancelResponseModal.isApproval ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Cancellation
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Reject Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archived Orders Section */}
      <div className="mt-8 border-t border-gray-200 pt-6">
        <button
          onClick={toggleArchived}
          className="flex items-center gap-2 text-lg font-serif font-semibold text-gray-500 hover:text-[#1a3a5c] transition-colors"
          data-testid="toggle-archived"
        >
          <Archive className="h-5 w-5" />
          <span>Archived Orders {archivedOrders.length > 0 ? `(${archivedOrders.length})` : ''}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${showArchived ? 'rotate-180' : ''}`} />
        </button>
        
        {showArchived && (
          <div className="mt-4 space-y-3" data-testid="archived-orders-list">
            {loadingArchived ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#C9A967] border-t-transparent mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading archived orders...</p>
              </div>
            ) : archivedOrders.length === 0 ? (
              <p className="text-sm text-gray-400 py-4">No archived orders.</p>
            ) : (
              archivedOrders.map((order) => (
                <div key={order.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`archived-order-${order.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-700">#{order.tracking_id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        order.order_status === 'delivered' ? 'bg-green-100 text-green-700' :
                        order.order_status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        order.order_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {order.order_status}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {order.payment_status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {order.items?.length || 0} item(s) • ₹{order.total_amount?.toFixed(2)} • {new Date(order.created_at).toLocaleDateString('en-IN')}
                      {order.archived_at && <span className="text-gray-400"> • Archived {new Date(order.archived_at).toLocaleDateString('en-IN')}</span>}
                    </p>
                  </div>
                  <button
                    onClick={() => unarchiveOrder(order.id)}
                    disabled={archivingOrder[order.id]}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#1a3a5c] bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                    data-testid={`unarchive-order-${order.id}`}
                  >
                    <ArchiveRestore className="h-4 w-4" />
                    {archivingOrder[order.id] ? 'Restoring...' : 'Unarchive'}
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
